// src/services/agencyService.js
import api from '../utils/api';// Your API instance

const AGENCY_BASE_URL = '/agencies';

const agencyService = {
  // Get all agencies with pagination, filtering, and search
  getAgencies: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add pagination
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      // Add search
      if (params.search) queryParams.append('search', params.search);
      
      // Add filters
      if (params.entityId) queryParams.append('entityId', params.entityId);
      if (params.isOpen !== undefined) queryParams.append('isOpen', params.isOpen);
      if (params.minRating) queryParams.append('minRating', params.minRating);
      if (params.city) queryParams.append('city', params.city);
      if (params.state) queryParams.append('state', params.state);
      
      // Location-based search
      if (params.lat && params.long) {
        queryParams.append('lat', params.lat);
        queryParams.append('long', params.long);
        if (params.radius) queryParams.append('radius', params.radius);
      }
      
      // Add sorting
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

      const response = await api.get(`${AGENCY_BASE_URL}?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get single agency by ID
  getAgencyById: async (id) => {
    try {
      const response = await api.get(`${AGENCY_BASE_URL}/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Create new agency
  createAgency: async (agencyData) => {
    try {
      const response = await api.post(AGENCY_BASE_URL, agencyData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update agency
  updateAgency: async (id, agencyData) => {
    try {
      const response = await api.put(`${AGENCY_BASE_URL}/${id}`, agencyData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Partial update agency
  patchAgency: async (id, agencyData) => {
    try {
      const response = await api.patch(`${AGENCY_BASE_URL}/${id}`, agencyData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete agency
  deleteAgency: async (id) => {
    try {
      const response = await api.delete(`${AGENCY_BASE_URL}/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get agency services
  getAgencyServices: async (agencyId) => {
    try {
      const response = await api.get(`${AGENCY_BASE_URL}/${agencyId}/services`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update agency services
  updateAgencyServices: async (agencyId, serviceIds) => {
    try {
      const response = await api.post(`${AGENCY_BASE_URL}/${agencyId}/services`, { serviceIds });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get opening hours
  getOpeningHours: async (agencyId) => {
    try {
      const response = await api.get(`${AGENCY_BASE_URL}/${agencyId}/opening-hours`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update opening hours
  updateOpeningHours: async (agencyId, hoursData) => {
    try {
      const response = await api.put(`${AGENCY_BASE_URL}/${agencyId}/opening-hours`, hoursData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get agency documents
  getAgencyDocuments: async (agencyId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      const response = await api.get(
        `${AGENCY_BASE_URL}/${agencyId}/documents?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Upload agency document
  uploadAgencyDocument: async (agencyId, file, documentData = {}) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Add metadata
      Object.keys(documentData).forEach(key => {
        formData.append(key, documentData[key]);
      });
      
      const response = await api.post(
        `${AGENCY_BASE_URL}/${agencyId}/documents`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete agency document
  deleteAgencyDocument: async (agencyId, documentId) => {
    try {
      const response = await api.delete(`${AGENCY_BASE_URL}/${agencyId}/documents/${documentId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Toggle agency status
  toggleAgencyStatus: async (id) => {
    try {
      const response = await api.patch(`${AGENCY_BASE_URL}/${id}/toggle-status`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Bulk delete agencies
  bulkDeleteAgencies: async (ids) => {
    try {
      const response = await api.delete(`${AGENCY_BASE_URL}/bulk`, { data: { ids } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Export agencies
  exportAgencies: async (format = 'csv', filters = {}) => {
    try {
      const response = await api.get(`${AGENCY_BASE_URL}/export/${format}`, {
        params: filters,
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get nearby agencies
  getNearbyAgencies: async (lat, long, radius = 10, filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('lat', lat);
      queryParams.append('long', long);
      queryParams.append('radius', radius);
      
      if (filters.services) queryParams.append('services', filters.services.join(','));
      if (filters.minRating) queryParams.append('minRating', filters.minRating);
      
      const response = await api.get(`${AGENCY_BASE_URL}/nearby?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get agency statistics
  getAgencyStats: async () => {
    try {
      const response = await api.get(`${AGENCY_BASE_URL}/stats`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

export default agencyService;